import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/Layout/UserLayout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell
} from '../../components/UI';
import { leadsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineUsers, HiOutlinePhone, HiOutlineEye
} from 'react-icons/hi';
import { format } from 'date-fns';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
  { value: 'converted', label: 'Converted' },
  { value: 'not_connected', label: 'Not Connected' },
];

const UserLeads = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  // Fetch my leads
  const { data: leadsData, isLoading } = useQuery({
    queryKey: ['myLeads', page, statusFilter],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await leadsAPI.getMy(params);
      return response.data || {};
    },
  });

  // Fetch leads counts
  const { data: counts } = useQuery({
    queryKey: ['userLeadsCounts'],
    queryFn: async () => {
      const response = await leadsAPI.getCounts();
      return response.data?.data || {};
    },
  });

  const leads = leadsData?.data || [];
  const pagination = leadsData?.pagination || {};

  const filteredLeads = search
    ? leads.filter(l =>
        l.name?.toLowerCase().includes(search.toLowerCase()) ||
        l.phone?.includes(search)
      )
    : leads;

  const totalLeads = counts ? Object.values(counts).reduce((a, b) => a + (b || 0), 0) : 0;

  return (
    <UserLayout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
            <p className="text-gray-500 text-sm mt-1">
              Total: {totalLeads} leads assigned to you
            </p>
          </div>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-4 gap-2">
          <div className="p-2 bg-blue-50 rounded-lg text-center">
            <p className="text-lg font-bold text-blue-600">{counts?.new || 0}</p>
            <p className="text-xs text-gray-600">New</p>
          </div>
          <div className="p-2 bg-yellow-50 rounded-lg text-center">
            <p className="text-lg font-bold text-yellow-600">{counts?.follow_up || 0}</p>
            <p className="text-xs text-gray-600">Follow Up</p>
          </div>
          <div className="p-2 bg-orange-50 rounded-lg text-center">
            <p className="text-lg font-bold text-orange-600">{counts?.hot || 0}</p>
            <p className="text-xs text-gray-600">Hot</p>
          </div>
          <div className="p-2 bg-green-50 rounded-lg text-center">
            <p className="text-lg font-bold text-green-600">{counts?.converted || 0}</p>
            <p className="text-xs text-gray-600">Converted</p>
          </div>
        </div>

        {/* Filters */}
        <Card padding="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search leads..."
              className="flex-1"
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="sm:w-40"
            />
          </div>
        </Card>

        {/* Leads List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUsers className="w-8 h-8 text-gray-400" />}
              title="No leads found"
              description="You don't have any leads assigned yet"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>Name</TableCell>
                    <TableCell header>Phone</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Created</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead._id}>
                      <TableCell>
                        <div className="font-medium text-gray-900">{lead.name}</div>
                        {lead.email && (
                          <div className="text-xs text-gray-500">{lead.email}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600">{lead.phone}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={lead.status} size="sm">
                          {lead.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm">
                          {lead.createdAt ? format(new Date(lead.createdAt), 'MMM d, yyyy') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<HiOutlinePhone className="w-4 h-4" />}
                            onClick={() => toast.error('Use mobile app to make calls')}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLeads.map((lead) => (
                <div key={lead._id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-medium">
                          {lead.name?.charAt(0)?.toUpperCase() || 'L'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{lead.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <HiOutlinePhone className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-500">{lead.phone}</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant={lead.status} size="sm">
                      {lead.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="mt-3 pl-13 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {lead.createdAt ? format(new Date(lead.createdAt), 'MMM d') : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </UserLayout>
  );
};

export default UserLeads;